import { useEffect, useRef, useState } from 'react'
import hljs from 'highlight.js/lib/core'
import yaml from 'highlight.js/lib/languages/yaml'
import 'highlight.js/styles/github.css'
import ErrorAlert from '../components/ErrorAlert.jsx'
import {
  CONTRACTS, fetchContract, workbenchImportUrl, WORKBENCH_IMPORT_ENABLED,
} from '../lib/api.js'
import { PAGE_STANDARD } from '../lib/layout.js'
import { BTN_PRIMARY } from '../lib/ui.js'

hljs.registerLanguage('yaml', yaml)

export default function ContractPage() {
  const [activeKey, setActiveKey] = useState(CONTRACTS[0].key)
  const [body, setBody] = useState('')
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const codeRef = useRef(null)

  const active = CONTRACTS.find((c) => c.key === activeKey) || CONTRACTS[0]
  const workbenchHref = workbenchImportUrl(active)

  useEffect(() => {
    let cancelled = false
    setBody('')
    setError(null)
    setCopied(false)
    fetchContract(active.inlineUrl)
      .then((text) => { if (!cancelled) setBody(text) })
      .catch((e) => { if (!cancelled) setError(String(e.message || e)) })
    return () => { cancelled = true }
  }, [active.inlineUrl])

  useEffect(() => {
    if (body && codeRef.current) hljs.highlightElement(codeRef.current)
  }, [body])

  async function copy() {
    try {
      await navigator.clipboard.writeText(body)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      setError('Could not copy to clipboard.')
    }
  }

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <span className="brand-gradient-text">Data contracts</span>
          </h1>
          <span className="inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-800">
            ODCS v3.2.0
          </span>
        </div>
        <p className="mt-2 text-base text-slate-600">
          next.actianlabs.com publishes its datasets as Open Data Contract
          Standard (ODCS) documents — hand-authored and the source of truth for
          schema, semantics, and quality rules. The live API is expected to
          conform; drift is treated as a bug.
        </p>
      </header>

      {/* The MCP server uses and loves these contracts. */}
      <section className="mb-6 rounded-2xl bg-brand-50 p-4 ring-1 ring-brand-200/60 sm:p-5">
        <h2 className="text-sm font-semibold text-brand-900">
          The MCP server loves these contracts
        </h2>
        <p className="mt-1 text-sm text-brand-900/80">
          The next.actianlabs.com MCP server publishes each ODCS contract as an
          MCP <em>resource</em>, so Claude can load it as <strong>context</strong> —
          not a tool call — before it answers. Grounding the model in the real
          schema and quality rules means sharper, better-shaped answers and
          fewer wasted round-trips.
        </p>
      </section>

      <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200" role="tablist" aria-label="Data contracts">
        {CONTRACTS.map((c) => {
          const selected = c.key === activeKey
          return (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActiveKey(c.key)}
              className={[
                'inline-flex min-h-[44px] items-center border-b-2 px-4 text-sm font-medium transition',
                selected
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
              ].join(' ')}
            >
              {c.label}
            </button>
          )
        })}
      </div>

      <p className="mb-3 text-sm text-slate-600">{active.blurb}</p>

      <ErrorAlert title="Couldn't load contract" message={error} source="ContractPage" className="mb-4" />

      <section className="mb-3 flex flex-wrap items-center gap-2">
        <a
          href={active.downloadUrl}
          className={BTN_PRIMARY}
          download
        >
          Download YAML
        </a>
        <button
          type="button"
          onClick={copy}
          disabled={!body}
          className="inline-flex min-h-[44px] items-center rounded-full px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
        {/* #348: hidden until the Workbench can actually accept an ODCS URL —
            svc#1180 and workbench.webapp#1307. A button that goes nowhere is
            worse than no button. */}
        {WORKBENCH_IMPORT_ENABLED && workbenchHref ? (
          <a
            href={workbenchHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center rounded-full px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
            title="Open this contract in your personal space on the Data Product Workbench"
          >
            View in Workbench ↗
          </a>
        ) : null}
      </section>

      <pre className="overflow-x-auto rounded-2xl bg-slate-50 p-4 text-xs ring-1 ring-slate-200/60 sm:text-sm">
        <code ref={codeRef} className="language-yaml">{body || 'Loading…'}</code>
      </pre>
    </main>
  )
}
